import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './CorrectionRequest.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CorrectionRequestForm({ transcriptId, transcriptData, audioDuration, onClose, onSuccess, showAlert }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cost, setCost] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [requestId, setRequestId] = useState(null);

  // Calculate cost on mount
  React.useEffect(() => {
    if (audioDuration) {
      const hours = audioDuration / 3600;
      const ratePerHour = 5.00;
      const calculatedCost = (hours * ratePerHour).toFixed(2);
      setCost(calculatedCost);
    }
  }, [audioDuration]);

  const handleCreateRequest = async () => {
    setIsProcessing(true);

    try {
      const response = await fetch('/api/corrections/request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcriptId,
          transcriptData,
          audioDurationSeconds: audioDuration
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create correction request');
      }

      const data = await response.json();
      setClientSecret(data.clientSecret);
      setRequestId(data.correctionRequest.id);
      setCost(data.cost);
    } catch (error) {
      console.error('Error creating request:', error);
      if (showAlert) {
        await showAlert('Request Failed', error.message);
      } else {
        alert(error.message);
      }
      setIsProcessing(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        if (showAlert) {
          await showAlert(
            'Request Submitted!',
            'Your correction request has been submitted. You will receive an email when your corrected transcript is ready.'
          );
        }
        onSuccess(requestId);
        onClose();
      }
    } catch (error) {
      console.error('Payment error:', error);
      if (showAlert) {
        await showAlert('Payment Failed', error.message);
      } else {
        alert(error.message);
      }
      setIsProcessing(false);
    }
  };

  const hours = (audioDuration / 3600).toFixed(2);

  if (!clientSecret) {
    return (
      <div className="correction-request-modal">
        <div className="correction-request-content">
          <button className="close-btn" onClick={onClose}>×</button>

          <h2>Request Professional Correction</h2>

          <div className="service-description">
            <p>Our professional annotators will review and correct your transcript for:</p>
            <ul>
              <li>Improved accuracy</li>
              <li>Proper punctuation and formatting</li>
              <li>Speaker identification</li>
              <li>Technical terminology corrections</li>
            </ul>
          </div>

          <div className="cost-breakdown">
            <h3>Cost Calculation</h3>
            <div className="cost-item">
              <span>Audio Duration:</span>
              <span>{hours} hours</span>
            </div>
            <div className="cost-item">
              <span>Rate:</span>
              <span>$5.00 per hour of audio</span>
            </div>
            <div className="cost-item total">
              <span>Total Cost:</span>
              <span>${cost}</span>
            </div>
          </div>

          <p className="notice">
            ⏱️ Typical turnaround time: 24-48 hours
          </p>

          <div className="button-group">
            <button
              onClick={handleCreateRequest}
              disabled={isProcessing}
              className="submit-btn"
            >
              {isProcessing ? 'Creating Request...' : 'Continue to Payment'}
            </button>
            <button onClick={onClose} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="correction-request-modal">
      <div className="correction-request-content">
        <button className="close-btn" onClick={onClose}>×</button>

        <h2>Payment for Professional Correction</h2>

        <div className="payment-summary">
          <p><strong>Request ID:</strong> #{requestId}</p>
          <p><strong>Amount:</strong> ${cost}</p>
        </div>

        <form onSubmit={handleSubmitPayment}>
          <div className="card-element-container">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>

          <p className="payment-notice">
            🔒 Secure payment powered by Stripe
          </p>

          <div className="button-group">
            <button
              type="submit"
              disabled={!stripe || isProcessing}
              className="submit-btn"
            >
              {isProcessing ? 'Processing...' : `Pay $${cost}`}
            </button>
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CorrectionRequest({ transcriptId, transcriptData, audioDuration, onClose, onSuccess, showAlert }) {
  // Check if feature is enabled
  const isEnabled = process.env.REACT_APP_ENABLE_HUMAN_CORRECTION === 'true';

  if (!isEnabled) {
    return null;
  }

  return (
    <Elements stripe={stripePromise}>
      <CorrectionRequestForm
        transcriptId={transcriptId}
        transcriptData={transcriptData}
        audioDuration={audioDuration}
        onClose={onClose}
        onSuccess={onSuccess}
        showAlert={showAlert}
      />
    </Elements>
  );
}

export default CorrectionRequest;